import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ImageIcon from '@mui/icons-material/Image';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import NotesIcon from '@mui/icons-material/Notes';
import PaymentsIcon from '@mui/icons-material/Payments';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Html5QrcodeScanner } from 'html5-qrcode';
import jsQR from 'jsqr';
import React, { useEffect, useRef, useState } from 'react';

import { getScannedTrailer } from '../../api/trailerApi';
import FileView from '../../components/FileUpload/FileView';
import useScreenMode from '../../Hooks/useScreenMode';
import { decryptToObject } from '../../Utils/encrypt';

type TrailerQrPayload = {
    type: 'trailer';
    v: number;
    id: string;
    trailer_no: string;
};

type TrailerDetails = {
    id: string;
    trailer_no: string;
    received_date: string;
    lease_agreement_no: string;
    rental_company: string;
    dclocation?: string;
    payer?: string;
    note?: string;
    cost?: number;
    info?: any;
    products: Array<{
        label: string;
        skid: string;
        description: string;
    }>;
    images: string[];
};

const formatDate = (value?: string) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toISOString().slice(0, 10);
};

const InfoItem = ({
    icon,
    label,
    value,
}: {
    icon: React.ReactNode;
    label: string;
    value?: React.ReactNode;
}) => (
    <Paper
        variant="outlined"
        sx={{
            p: 1.5,
            borderRadius: 2,
            height: '100%',
        }}
    >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Avatar
                sx={{
                    width: 34,
                    height: 34,
                    bgcolor: 'primary.lighter',
                    color: 'primary.main',
                }}
            >
                {icon}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ wordBreak: 'break-word' }}
                >
                    {value || 'N/A'}
                </Typography>
            </Box>
        </Stack>
    </Paper>
);

const EmptyState = ({
    title,
    subtitle,
}: {
    title: string;
    subtitle: string;
}) => (
    <Stack
        spacing={1}
        alignItems="center"
        justifyContent="center"
        sx={{ py: 6, textAlign: 'center' }}
    >
        <QrCodeScannerIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h6" fontWeight={700}>
            {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            {subtitle}
        </Typography>
    </Stack>
);

export default function ScannerPage() {
    const { isMobileLike } = useScreenMode();
    const scannerRef = useRef<Html5QrcodeScanner | null>(null);
    const [loading, setLoading] = useState(false);
    const [startingScanner, setStartingScanner] = useState(false);
    const [error, setError] = useState('');
    const [scannedData, setScannedData] = useState<TrailerDetails | null>(null);

    const stopScanner = async () => {
        try {
            if (scannerRef.current) {
                await scannerRef.current.clear();
                scannerRef.current = null;
            }
        } catch (err) {
            console.warn('Error clearing scanner:', err);
        }
    };

    const fetchTrailerDetails = async (payload: TrailerQrPayload) => {
        try {
            setLoading(true);
            setError('');

            const data = await getScannedTrailer(payload);

            setScannedData(data || null);
        } catch (err: any) {
            console.error(err);
            setError(err?.message || 'Failed to fetch trailer details.');
            setScannedData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleDecodedResult = async (raw: string) => {
        try {
            await stopScanner();

            const decoded = decryptToObject(raw) as TrailerQrPayload | null;

            if (
                !decoded ||
                decoded.type !== 'trailer' ||
                !decoded.id ||
                !decoded.trailer_no
            ) {
                setError('Invalid QR code.');
                return;
            }

            await fetchTrailerDetails(decoded);
        } catch (err) {
            console.error(err);
            setError('Unable to decode QR code.');
        }
    };

    const errorHandler = (err: any) => {
        console.warn(err);
    };

    const initializeScanner = async () => {
        setStartingScanner(true);
        setError('');

        await stopScanner();

        const mountScanner = () => {
            const element = document.getElementById('reader');
            if (!element) {
                setStartingScanner(false);
                return;
            }

            const qrScanner = new Html5QrcodeScanner(
                'reader',
                {
                    qrbox: isMobileLike
                        ? { width: 220, height: 220 }
                        : { width: 280, height: 280 },
                    fps: 10,
                    rememberLastUsedCamera: true,
                    supportedScanTypes: [0],
                },
                true
            );

            qrScanner.render(
                async (result) => {
                    await handleDecodedResult(result);
                },
                errorHandler
            );

            scannerRef.current = qrScanner;
            setStartingScanner(false);
        };

        setTimeout(mountScanner, 120);
    };

    useEffect(() => {
        if (!scannedData) {
            initializeScanner();
        }

        return () => {
            stopScanner();
        };
    }, [isMobileLike]);

    const resetScanner = async () => {
        setScannedData(null);
        setError('');
        setLoading(false);
        await initializeScanner();
    };

    const handleImageScan = (imageFile: File | null) => {
        if (!imageFile) return;

        const reader = new FileReader();

        reader.onload = () => {
            const image = new Image();
            image.src = reader.result as string;

            image.onload = async () => {
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');

                if (!context) {
                    setError('Could not process image.');
                    return;
                }

                canvas.width = image.width;
                canvas.height = image.height;
                context.drawImage(image, 0, 0, image.width, image.height);

                const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(imageData.data, canvas.width, canvas.height);

                if (code?.data) {
                    await handleDecodedResult(code.data);
                } else {
                    setError('No QR code found in the selected image.');
                }
            };
        };

        reader.readAsDataURL(imageFile);
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
            <Stack spacing={2.5}>
                <Box>
                    <Typography variant="h4" fontWeight={800}>
                        Trailer Scanner
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Open the camera, scan the trailer QR, and load the latest trailer
                        information with products and images.
                    </Typography>
                </Box>

                {error ? <Alert severity="error">{error}</Alert> : null}

                {!scannedData ? (
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, lg: 8 }}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: 'none',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        background:
                                            'linear-gradient(180deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.02) 100%)',
                                    }}
                                >
                                    <Stack direction="row" spacing={1.2} alignItems="center">
                                        <QrCodeScannerIcon color="primary" />
                                        <Box>
                                            <Typography variant="h6" fontWeight={700}>
                                                Live Camera Scan
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Point your camera at the printed trailer QR code.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>

                                <CardContent>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            minHeight: { xs: 320, md: 420 },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: '#fafafa',
                                        }}
                                    >
                                        <Box sx={{ width: '100%', p: 1.5 }}>
                                            <div id="reader" style={{ width: '100%' }} />

                                            {startingScanner ? (
                                                <Stack
                                                    spacing={1.5}
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    sx={{ py: 6 }}
                                                >
                                                    <CircularProgress size={28} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Starting camera...
                                                    </Typography>
                                                </Stack>
                                            ) : null}
                                        </Box>
                                    </Paper>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 4 }}>
                            <Stack spacing={2}>
                                <Card
                                    sx={{
                                        borderRadius: 3,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: 'none',
                                    }}
                                >
                                    <CardContent>
                                        <Stack spacing={2}>
                                            <Stack direction="row" spacing={1.2} alignItems="center">
                                                <ImageSearchIcon color="primary" />
                                                <Typography variant="h6" fontWeight={700}>
                                                    Scan from Image
                                                </Typography>
                                            </Stack>

                                            <Typography variant="body2" color="text.secondary">
                                                Upload a QR image from your phone or gallery if camera
                                                scanning is not convenient.
                                            </Typography>

                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<CameraAltIcon />}
                                            >
                                                Upload QR Image
                                                <input
                                                    hidden
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) =>
                                                        handleImageScan(e.target.files?.[0] || null)
                                                    }
                                                />
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>

                                <Card
                                    sx={{
                                        borderRadius: 3,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: 'none',
                                    }}
                                >
                                    <CardContent>
                                        <Stack spacing={1.25}>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                Scanning Tips
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Use the rear camera for sharper focus.
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Keep the QR label flat and fully visible.
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Move closer if the QR appears small.
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Scanning stops automatically after a successful read.
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Stack>
                        </Grid>
                    </Grid>
                ) : loading ? (
                    <Card
                        sx={{
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 'none',
                        }}
                    >
                        <CardContent>
                            <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
                                <CircularProgress />
                                <Typography variant="body1" fontWeight={700}>
                                    Fetching trailer details...
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                ) : (
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, xl: 8 }}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: 'none',
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.75,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        background:
                                            'linear-gradient(180deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.02) 100%)',
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.5}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    >
                                        <Stack direction="row" spacing={1.2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    bgcolor: 'primary.main',
                                                    width: 42,
                                                    height: 42,
                                                }}
                                            >
                                                <LocalShippingIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h5" fontWeight={800}>
                                                    {scannedData.trailer_no}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Trailer details loaded successfully.
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Chip
                                                label={`Received: ${formatDate(scannedData.received_date)}`}
                                                color="primary"
                                                variant="outlined"
                                            />
                                            <Button
                                                variant="outlined"
                                                startIcon={<RestartAltIcon />}
                                                onClick={resetScanner}
                                            >
                                                Scan Again
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Box>

                                <CardContent>
                                    {scannedData.products?.length ? (
                                        <Stack spacing={1.25}>
                                            {scannedData.products.map((product, index) => (
                                                <Paper
                                                    key={index}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    <Grid container spacing={1.5}>
                                                        <Grid size={{ xs: 12, md: 3 }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Product
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight={700}>
                                                                {product.label || 'N/A'}
                                                            </Typography>
                                                        </Grid>

                                                        <Grid size={{ xs: 12, md: 2 }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Skid
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight={700}>
                                                                {product.skid || 'N/A'}
                                                            </Typography>
                                                        </Grid>

                                                        <Grid size={{ xs: 12, md: 7 }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Description
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {product.description || 'N/A'}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <EmptyState
                                            title="No Products"
                                            subtitle="There are no products attached to this trailer."
                                        />
                                    )}
                                </CardContent>
                            </Card>

                            <Card
                                sx={{
                                    mt: 2,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: 'none',
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={700}>
                                        Trailer Info
                                    </Typography>
                                </Box>
                                <CardContent>
                                    <Stack spacing={2}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                                <InfoItem
                                                    icon={<AssignmentIcon fontSize="small" />}
                                                    label="Lease Agreement"
                                                    value={scannedData.lease_agreement_no}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                                <InfoItem
                                                    icon={<BusinessIcon fontSize="small" />}
                                                    label="Rental Company"
                                                    value={scannedData.rental_company}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                                <InfoItem
                                                    icon={<WarehouseIcon fontSize="small" />}
                                                    label="DC Location"
                                                    value={scannedData.dclocation}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                                <InfoItem
                                                    icon={<PaymentsIcon fontSize="small" />}
                                                    label="Payer"
                                                    value={scannedData.payer}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                                <InfoItem
                                                    icon={<Inventory2Icon fontSize="small" />}
                                                    label="Products"
                                                    value={scannedData.products?.length || 0}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                                <InfoItem
                                                    icon={<ImageIcon fontSize="small" />}
                                                    label="Images"
                                                    value={scannedData.images?.length || 0}
                                                />
                                            </Grid>

                                            {scannedData.cost !== undefined && (
                                                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                                    <InfoItem
                                                        icon={<PaymentsIcon fontSize="small" />}
                                                        label="Cost"
                                                        value={scannedData.cost}
                                                    />
                                                </Grid>
                                            )}

                                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                                <InfoItem
                                                    icon={<CalendarMonthIcon fontSize="small" />}
                                                    label="Received Date"
                                                    value={formatDate(scannedData.received_date)}
                                                />
                                            </Grid>
                                        </Grid>

                                        {scannedData.note ? (
                                            <>
                                                <Divider />
                                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                    <Stack direction="row" spacing={1.2} alignItems="center" mb={1}>
                                                        <NotesIcon fontSize="small" color="primary" />
                                                        <Typography variant="subtitle1" fontWeight={700}>
                                                            Notes
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {scannedData.note}
                                                    </Typography>
                                                </Paper>
                                            </>
                                        ) : null}
                                    </Stack>
                                </CardContent>

                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, xl: 4 }}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: 'none',
                                    height: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={700}>
                                        Images
                                    </Typography>
                                </Box>

                                <CardContent>
                                    <Box
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            p: 1.5,
                                            minHeight: 360,
                                            bgcolor: '#fafafa',
                                        }}
                                    >
                                        {scannedData.images?.length ? (
                                            <FileView
                                                id=""
                                                fetchApi="/uploads/trailers/"
                                                deleteApi="/trailer/deleteImage"
                                                files={scannedData.images}
                                            />
                                        ) : (
                                            <EmptyState
                                                title="No Images"
                                                subtitle="No images are available for this trailer."
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Stack>
        </Box>
    );
}